// Rich-text helpers — keep pasted formatting (colors, fonts, highlights,
// backgrounds…) on description + comment fields, safely.
//
// We store SANITIZED HTML in the existing text columns (tickets.description,
// card_comments.body) so the change is backward-compatible: pre-existing
// plain-text rows render exactly the same when fed back through innerHTML.
//
// htmlToText is the inverse helper used by snippet/search/email surfaces that
// can only show plain text. Anything user-facing that is NOT a rich renderer
// MUST funnel through htmlToText to avoid leaking tags into the UI.

import DOMPurify from 'dompurify'

const ALLOWED_TAGS = [
  'p',
  'div',
  'span',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'strike',
  'mark',
  'ul',
  'ol',
  'li',
  'a',
  'code',
  'pre',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'sub',
  'sup',
  'font', // legacy paste from Word/Google Docs
]

// Style attribute carries the formatting the user pasted. DOMPurify's internal
// CSS parser blocks unsafe properties (url()/expression/behavior/etc.).
const ALLOWED_ATTR = ['href', 'target', 'rel', 'style', 'class', 'color', 'face', 'size']

/** Sanitize HTML for storage / rendering. */
export function sanitizeRichHtml(html: string | null | undefined): string {
  if (!html) return ''
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Force-rewrite <a> to safe targets.
    ADD_ATTR: ['target', 'rel'],
  })
}

/** Extract plain-text content from HTML. Newlines from block-level tags +
 *  <br> are preserved so the result reads naturally in plain UIs. Pre-
 *  existing plain text passes through unchanged. */
export function htmlToText(html: string | null | undefined): string {
  if (!html) return ''
  // Detect if it's plain text already (no tags) — common for legacy rows.
  if (!/[<&]/.test(html)) return html
  try {
    const tmp = document.createElement('div')
    // Use sanitized HTML so we don't execute anything if the source happens
    // to be malicious (defense-in-depth — these strings come from our own DB
    // but it's still HTML being assigned to innerHTML).
    tmp.innerHTML = sanitizeRichHtml(html)
    // Insert real newlines for block tags / <br> so the plain output
    // matches the visible layout.
    tmp.querySelectorAll('br').forEach((br) => br.replaceWith('\n'))
    tmp
      .querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, blockquote, pre')
      .forEach((el) => el.append('\n'))
    return (tmp.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim()
  } catch {
    return html
  }
}

/** True when the HTML carries no visible text (just empty tags / whitespace).
 *  Used to gate "is this draft empty" checks like the comment composer. */
export function isRichEmpty(html: string | null | undefined): boolean {
  return htmlToText(html).trim().length === 0
}
